import type { CalculatorContent, Faq } from '@/types/calculator'
import { fuelDefaults } from '@/data/defaults'
import type { FuelCostInputs } from './types'

export const content: CalculatorContent<FuelCostInputs> = {
  intro:
    'Work out your petrol cost per kilometre and monthly fuel cost with this fuel cost calculator for South Africa. Enter a trip distance, your car’s consumption and the pump price to see the cost per trip, per period and per year.',
  howItWorks: [
    'Fuel used per trip is the distance multiplied by your consumption in litres per 100 km. Multiplying by the fuel price gives the Rand cost per trip.',
    `Costs are then scaled by the number of trips per period you choose — day, week, month or year. A daily commute is treated as roughly five trips a week; weekly, monthly and yearly periods use 52, 12 and 1 multiplier respectively.`,
    `The default fuel price (R${fuelDefaults.defaultFuelPrice} per litre) is a bundled estimate from ${fuelDefaults.sourceName} — always replace it with your local pump price, since South African fuel prices change monthly.`,
  ],
  formulas: [
    {
      label: 'Litres per trip',
      expression: 'distance (km) × consumption (L/100km) ÷ 100',
    },
    {
      label: 'Cost per trip',
      expression: 'litres per trip × fuel price per litre',
    },
    {
      label: 'Cost per period',
      expression: 'cost per trip × trips per period',
    },
    {
      label: 'Annual cost',
      expression: 'cost per trip × trips per period × periods per year',
    },
  ],
  example: {
    title: 'A 30 km commute, 20 trips a month',
    description:
      'A driver commuting 30 km per trip in a car using 7.5 L/100km, making 20 trips a month at the default fuel price.',
    inputs: {
      distanceKm: 30,
      consumptionLPer100km: 7.5,
      fuelPricePerLitre: fuelDefaults.defaultFuelPrice,
      tripsPerPeriod: 20,
      period: 'month',
    },
  },
  definitions: [
    {
      term: 'L/100km',
      description:
        'Litres of fuel used to travel 100 km — the standard consumption measure in South Africa. Lower is better.',
    },
    {
      term: 'Petrol cost per km',
      description:
        'The fuel price multiplied by your consumption, divided by 100. Useful for comparing cars or quoting travel costs.',
    },
    {
      term: 'Pump price',
      description:
        'The regulated retail price of petrol or diesel per litre, adjusted monthly by the DMRE based on the Rand and oil price.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'How do I calculate petrol cost per km?',
    answer:
      'Multiply your consumption (L/100km) by the fuel price and divide by 100. For example, at 7.5 L/100km the calculator shows the per-trip and per-period cost automatically.',
  },
  {
    question: 'Which fuel price should I use?',
    answer: `Use your local pump price. The default of R${fuelDefaults.defaultFuelPrice}/L is a bundled estimate and South African fuel prices change monthly.`,
  },
  {
    question: 'Does the calculator work for diesel cars?',
    answer: 'Yes — just enter the diesel price per litre and your car’s consumption figure.',
  },
  {
    question: 'How is the monthly fuel cost worked out?',
    answer:
      'Monthly cost = distance per trip × consumption ÷ 100 × fuel price × number of trips per month.',
  },
]
